import React from 'react';
import {
    FormControl,
    FormLabel,
    Input,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
    Select,
    Switch,
    InputGroup,
    InputRightAddon,
    Text,
    HStack,
} from '@chakra-ui/react';
import { SuDateInput } from '../common/SuDateInput';
import { CustomFieldType, UserCustomFieldValue } from '../../types/customFields';
import { getCurrencySymbol } from '../../utils/customFieldsUtils';
import { useIntl } from 'react-intl';

// Custom Field Input Props
export interface CustomFieldInputProps {
    field: UserCustomFieldValue;
    value: any;
    onChange: (value: any) => void;
    onBlur?: () => void;
    error?: string;
    isDisabled?: boolean;
    isRequired?: boolean;
    containerRef?: React.MutableRefObject<null>;
}

/**
 * Dynamic input component that renders the appropriate input type
 * based on the custom field definition
 */
export const CustomFieldInput: React.FC<CustomFieldInputProps> = ({
    field,
    value,
    onChange,
    onBlur,
    error,
    isDisabled = false,
    isRequired = false,
    containerRef,
}) => {
    const int = useIntl();

    const renderInput = () => {
        switch (field.fieldType) {
            case CustomFieldType.NUMBER:
                return (
                    <NumberInput
                        value={value || ''}
                        onChange={(valueString) => onChange(valueString)}
                        onBlur={onBlur}
                        isDisabled={isDisabled}
                        min={field.validationRules?.min}
                        max={field.validationRules?.max}
                    >
                        <NumberInputField />
                        <NumberInputStepper>
                            <NumberIncrementStepper />
                            <NumberDecrementStepper />
                        </NumberInputStepper>
                    </NumberInput>
                );

            case CustomFieldType.DECIMAL:
                const currencySymbol = getCurrencySymbol(field.fieldName);
                return (
                    <InputGroup>
                        <NumberInput
                            value={value || ''}
                            onChange={(valueString) => onChange(valueString)}
                            onBlur={onBlur}
                            isDisabled={isDisabled}
                            min={field.validationRules?.min}
                            max={field.validationRules?.max}
                            precision={field.validationRules?.decimalPlaces || 2}
                            step={0.01}
                            flex="1"
                        >
                            <NumberInputField />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                        {currencySymbol && <InputRightAddon>{currencySymbol}</InputRightAddon>}
                    </InputGroup>
                );

            case CustomFieldType.SELECT:
                return (
                    <Select
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        onBlur={onBlur}
                        isDisabled={isDisabled}
                        placeholder={int.formatMessage({ id: 'form.select' })}
                    >
                        {field.selectOptions?.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Select>
                );

            case CustomFieldType.TEXT:
                return (
                    <Input
                        value={value || ''}
                        onChange={(e) => onChange(e.target.value)}
                        onBlur={onBlur}
                        isDisabled={isDisabled}
                        maxLength={field.validationRules?.maxLength}
                        placeholder="-"
                    />
                );

            case CustomFieldType.DATE:
                return (
                    <SuDateInput
                        value={value ? new Date(value) : undefined}
                        onChange={(date) => onChange(date?.toISOString() || null)}
                        size="md"
                        containerRef={containerRef}
                    />
                );

            case CustomFieldType.BOOLEAN:
                return (
                    <HStack spacing={3}>
                        <Switch
                            isChecked={value || false}
                            onChange={(e) => onChange(e.target.checked)}
                            onBlur={onBlur}
                            isDisabled={isDisabled}
                            colorScheme="primary"
                        />
                        <Text fontSize="sm" color="gray.500">
                            {value ? 'Yes' : 'No'}
                        </Text>
                    </HStack>
                );

            default:
                return (
                    <Text color="red.500" fontSize="sm">
                        Unsupported field type: {field.fieldType}
                    </Text>
                );
        }
    };

    return (
        <FormControl isInvalid={!!error} isRequired={isRequired}>
            <FormLabel htmlFor={`field-${field.id}`}>
                {field.displayName}
                {field.description && (
                    <Text fontSize="xs" color="gray.500" fontWeight="normal" mt={1}>
                        {field.description}
                    </Text>
                )}
            </FormLabel>

            {renderInput()}

            {error && (
                <Text color="red.500" fontSize="sm" mt={1}>
                    {error}
                </Text>
            )}
        </FormControl>
    );
};

export default CustomFieldInput;
